import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Customer } from '../../../models/consumer';
import { LineItems } from '../../../models/contract-model';
import { AccountSummaryService } from '../../../services/account-summary.service';
import { setCustomerCurrency } from '../../app-wide/currencycode/currencycode.actions';
import { CustomerSummaryActionTypes } from '../customer-summary/customer-summary.actions';
/* eslint-disable @typescript-eslint/naming-convention  */
import * as CustomerActions from './customer.actions';
import { CustomerService } from '../../../services/customer.service';
import { AppFacadeService } from '../../../services/app-facade.service';
import { AssignOrRevokePermissionRequest } from '../../../admin/interfaces/user-accout.interface';
import { ExternalProviderService } from '../../../services/external-provider.service';
import { NotificationService } from '../../../services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { TrackingResponse } from '../../../models/tracking-result.model';

@Injectable()
export class CustomerEffects {
    private readonly actions$ = inject(Actions);
    private readonly summaryServices = inject(AccountSummaryService);
    private readonly customerService = inject(CustomerService);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly externalProviderService = inject(ExternalProviderService);
    private readonly notificationService = inject(NotificationService);
    private readonly translateService = inject(TranslateService);

    loadCustomers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerSummaryActionTypes.LoadCustomer),
            switchMap((action: { type: string; payload: { id: string } }) =>
                this.summaryServices.getCustomer(action.payload?.id ?? '').pipe(
                    map(customer => ({ type: CustomerSummaryActionTypes.LoadCustomerFinished, customer: customer })),
                    catchError(() => EMPTY)
                )
            )
        )
    );

    handleLoadedCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerSummaryActionTypes.LoadCustomerFinished),
            map((action: { customer: Customer }) => {
                const currencyCode = action.customer.CurrencyCode || '';
                return setCustomerCurrency({ currencyCode });
            })
        )
    );

    loadCustomerSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerSummaryActionTypes.LoadCustomerSummary),
            exhaustMap((action: { type: string; payload: { id: string } }) =>
                this.summaryServices.getCustomerSummary(action.payload?.id ?? '').pipe(
                    map(summary => ({ type: CustomerSummaryActionTypes.LoadCustomerSummaryFinished, summary: summary })),
                    catchError(() => of({ type: CustomerSummaryActionTypes.LoadCustomerSummaryError }))
                )
            )
        )
    );

    loadCustomerContacts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.loadCustomerContacts),
            switchMap(({ customerId }) =>
                this.customerService.getCustomerContacts(customerId).pipe(
                    map(contacts => {
                        if (!contacts?.length) {
                            this.notificationService.error(this.translateService.instant('noContactsFoundForCustomer'));
                        }
                        return CustomerActions.loadCustomerContactsFinished({ contacts });
                    }),
                    catchError(() => of(CustomerActions.loadCustomerContactsFailed())),
                    tap(() => this.appFacadeService.setLoading(false))
                )
            )
        )
    );

    assignOrRevokePermission$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CustomerActions.assignOrRevokePermission),
                withLatestFrom(this.appFacadeService.getCustomerContacts()),
                switchMap(([action, contacts]) => {
                    const data = action.data;
                    const matchedContact = contacts?.find(contact => contact.email?.toLowerCase() === data.email.toLowerCase());
                    const updatedPermissions = { ...matchedContact?.permissions } as Record<string, boolean>;
                    const request: AssignOrRevokePermissionRequest = {
                        email: data.email,
                        customerId: data.customerId,
                        permissions: updatedPermissions as AssignOrRevokePermissionRequest['permissions']
                    };
                    return this.customerService.updateCustomerContactPermissions(request).pipe(
                        catchError(() => {
                            return EMPTY;
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    setContactActiveStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.setContactActiveStatus),
            switchMap(({ customerId, contactId, isActive }) =>
                this.customerService.setContactActiveStatus(customerId, contactId, isActive).pipe(
                    map(() => CustomerActions.loadCustomerContacts({ customerId })),
                    catchError(() => {
                        this.notificationService.error(this.translateService.instant('somethingWentWrong'));
                        return of(CustomerActions.loadCustomerContactsFailed());
                    })
                )
            )
        )
    );

    loadLiveLink$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.loadLiveLink),
            withLatestFrom(this.appFacadeService.getLiveLinkUrl(), this.appFacadeService.getLivelinkExpiry()),
            switchMap(([action, cachedUrl, cachedExpiry]) => {
                // Check if cached livelink is still valid
                if (cachedUrl && cachedExpiry) {
                    const expiryDate = new Date(cachedExpiry);
                    const now = new Date();
                    if (expiryDate > now) {
                        // Cached livelink is still valid
                        return of(CustomerActions.loadLiveLinkFinished({ livelinkUrl: cachedUrl, livelinkExpiry: cachedExpiry }));
                    }
                }

                // Request new livelink
                return this.externalProviderService.getLivelinkUrl(action.customerId.toString()).pipe(
                    map((response: { livelink: string; expiresAt: string }) =>
                        CustomerActions.loadLiveLinkFinished({
                            livelinkUrl: response.livelink,
                            livelinkExpiry: response.expiresAt
                        })
                    ),
                    catchError(error => {
                        const errorToken = error.error?.token;
                        const message = errorToken === 'HAPN_CREDS_NOT_FOUND' ? this.translateService.instant('hapnCredsNotConfigured') : this.translateService.instant('gpsServiceError');
                        this.notificationService.error(message);
                        return of(
                            CustomerActions.loadLiveLinkFinished({
                                livelinkUrl: undefined,
                                livelinkExpiry: undefined
                            })
                        );
                    })
                );
            })
        )
    );
}
